import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Search, ShoppingCart, ShoppingBasket } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { basket } from "@/lib/basket";
import { OrderSummary } from "@/components/OrderSummary";
import { BundleSuggest } from "@/components/Related";
import { checkout } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

const chip = (on, dis) => `px-3 py-1.5 rounded-md border text-xs transition-colors text-left ${dis ? "border-slate-800 text-slate-600 cursor-not-allowed" : on ? "border-[#1074BA] bg-[#1074BA]/15 text-white" : "border-slate-700 text-slate-400 hover:text-white"}`;
const fullyOwned = (p) => p.owned && !p.owned_regions;

function ProductPicker({ cat, selected, setSelected }) {
  const [q, setQ] = useState("");
  const list = cat.products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (n) => setSelected(selected.includes(n) ? selected.filter((x) => x !== n) : [...selected, n]);
  return (
    <div>
      <p className="font-mono text-[11px] uppercase text-slate-500 mb-2">1 · Products ({cat.products.length} available)</p>
      <div className="relative mb-2"><Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="subpanel-search" className="w-full h-9 rounded-md bg-slate-900 border border-slate-700 pl-8 pr-3 text-sm" /></div>
      <div className="grid sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1" data-testid="subpanel-products">
        {list.map((p) => (
          <button key={p.name} disabled={fullyOwned(p)} onClick={() => toggle(p.name)} data-testid={`subpanel-product-${p.name}`} className={chip(selected.includes(p.name), fullyOwned(p))}>
            <span className="flex items-center justify-between gap-2"><span className="truncate">{p.name}</span>
              {p.owned ? <span className="text-[10px] text-[#03AB9E] flex items-center gap-1"><Check className="h-3 w-3" />Owned{p.owned_regions ? ` · ${p.owned_regions.length} reg.` : ""}</span>
                : p.sample && <span className="text-[10px] text-[#F58B23]">Free sample</span>}</span>
          </button>
        ))}
        {!list.length && <p className="text-xs text-slate-500">No products match "{q}"</p>}
      </div>
    </div>
  );
}

function RegionPicker({ regions, value, onChange }) {
  const toggle = (r) => {
    if (r === "Global") return onChange(["Global"]);
    const cur = value.filter((x) => x !== "Global");
    const next = cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r];
    onChange(next.length ? next : ["Global"]);
  };
  return (
    <div>
      <p className="font-mono text-[11px] uppercase text-slate-500 mb-2">2 · Regions <span className="normal-case text-slate-600">· each region = 1/6 of the Global price, capped at Global</span></p>
      <div className="flex flex-wrap gap-1.5">{["Global", ...regions].map((r) => <button key={r} onClick={() => toggle(r)} data-testid={`subpanel-region-${r}`} className={chip(value.includes(r))}>{r === "Global" ? "Global (all regions)" : r}</button>)}</div>
    </div>
  );
}

export function SubscribePanel({ module, cat, initial = [], collapsible = false, coupon = "", onDone }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const { currency, country } = useCurrency();
  const [open, setOpen] = useState(!collapsible);
  const [selected, setSelected] = useState(initial);
  const [regions, setRegions] = useState(["Global"]);
  const [quote, setQuote] = useState(null);
  const [agree, setAgree] = useState(false);
  const [billing, setBilling] = useState({});
  const hasRegions = cat.regions.length > 0;
  const regs = hasRegions ? regions : ["Global"];

  const body = { subs: selected.map((p) => ({ module, products: [p], regions: regs, coupon })), reports: [] };
  useEffect(() => {
    if (!selected.length) return setQuote(null);
    api.post("/checkout/basket-quote", body).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, selected, regions, coupon, user]);

  const done = () => { qc.invalidateQueries(); setSelected([]); onDone?.(); };
  const suggest = (s) => {
    if (s.module === module) return setSelected([...selected, s.product]);
    basket.addSubs(s.module, [s.product], ["Global"]);
    toast.success(`${s.product} · ${s.label} added to basket`);
  };
  const add = () => { basket.addSubs(module, selected, regs); toast.success(`${selected.length} item${selected.length > 1 ? "s" : ""} added to basket`); setSelected([]); };
  const pay = () => (user ? checkout({ type: "basket", accept_terms: agree, currency, billing: { ...billing, country }, basket: body }, done)
    : nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`));

  return (
    <section className="bg-[#0F172A]/90 border border-[#F58B23]/40 rounded-lg fade-up" data-testid={`subscribe-panel-${module}`}>
      <button onClick={() => setOpen(!open)} disabled={!collapsible} data-testid="subpanel-toggle" className="w-full flex items-center gap-3 px-5 py-4 text-left">
        <span className="h-8 w-8 rounded-md bg-[#F58B23]/15 grid place-items-center"><ShoppingCart className="h-4 w-4 text-[#F58B23]" /></span>
        <span className="flex-1"><span className="block font-semibold">{collapsible ? "Add products / regions" : `Subscribe to ${cat.label}`}</span>
          <span className="block text-xs text-slate-400">{hasRegions ? "Pick products and Global or specific regions. Annual access, starts instantly after payment." : "Pick products. Annual global access, starts instantly after payment."}</span></span>
        {collapsible && <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <div className="px-5 pb-5 grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-5">
            <ProductPicker cat={cat} selected={selected} setSelected={setSelected} />
            {hasRegions && <RegionPicker regions={cat.regions} value={regions} onChange={setRegions} />}
            {selected.length > 0 && <BundleSuggest subs={body.subs} count={quote?.count} nextTier={quote?.next_tier} onAdd={suggest} />}
          </div>
          <OrderSummary quote={quote} onPay={pay} user={user} billing={billing} setBilling={setBilling} agree={agree} setAgree={setAgree} country={country}
            payLabel={user ? "Pay now" : "Login / Register to subscribe"}
            extra={<button onClick={add} disabled={!quote || quote.total_usd <= 0} data-testid="subpanel-add-basket" className="mt-2 w-full h-10 rounded-md border border-slate-700 hover:border-[#F58B23] text-sm flex items-center justify-center gap-2 disabled:opacity-50"><ShoppingBasket className="h-4 w-4" />Add to basket</button>} />
        </div>
      )}
    </section>
  );
}
